import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import ApiError from 'components/common/ApiError';
import { getMonthLabel } from 'utils';
import { Card, CardHeader, CardBody, Col, Row, ButtonGroup } from "reactstrap";
import { faCalendarDays, faCalendarWeek } from '@fortawesome/free-solid-svg-icons';
import "./TokenUsageHistogram.css";
import {DiagramButton, DiagramBody, MaximizeButton, DiagramHeader} from './DiagramButton';
import { API_ENDPOINTS } from 'constants';
import useHistogramData from 'hooks/useHistogramData';
import { DIAGRAMS } from 'constants';

const TokenUsageHistogram = ({ isMaximized, toggleMaximize }) => {
    const { t, i18n } = useTranslation();

    const processDataForChart = useCallback((data, category) => {
        const chartData = category === DIAGRAMS.TOKENS.MONTHLY ? data?.ai_usage_monthly : data?.ai_usage_daily;
        return chartData?.reduce((acc, item) => ({
            labels: [
                ...acc.labels,
                category === DIAGRAMS.TOKENS.MONTHLY ? getMonthLabel(item.date, i18n.language) : item.date.substring(0, 10)
            ],
            dataEdited: [...acc.dataEdited, item.total_tokens],
            totalTokens: acc.totalTokens + item.total_tokens,
        }), { labels: [], dataEdited: [], totalTokens: 0 }) || { labels: [], dataEdited: [], totalTokens: 0 };
    }, [i18n.language]);

    const { chartData, isLoading, error, refetch, category, setCategory, gradientStroke} = useHistogramData(API_ENDPOINTS.REPORTS.AI_USAGE, DIAGRAMS.TOKENS.MONTHLY, processDataForChart)

    const formattedChartData = useMemo(() => ({
        labels: chartData.labels,
        datasets: [{
            label: t('used.ai.token'),
            fill: true,
            backgroundColor: gradientStroke,
            borderColor: "#1f8ef1",
            borderWidth: 2,
            pointBackgroundColor: "#1f8ef1",
            data: chartData.dataEdited,
        }],
    }), [chartData, gradientStroke, t]);

    if(error) return <ApiError retryFetch={refetch} errorMessage={error.message}/>
    return (
        <Card className={`card-chart${isMaximized ? ' maximized-chart' : ''}`}>
            <CardHeader>
                <Row>
                    <DiagramHeader headerText={t('token.usage')} title={chartData.totalTokens} isLoading={isLoading} icon="tim-icons icon-bell-55 text-info"/>
                    <Col sm="6">
                        <ButtonGroup className="btn-group-toggle float-right" data-toggle="buttons">
                            <DiagramButton id="1" buttonCategory="monthly" setCategory={setCategory} category={category} label={t('diagrams.period.monthly')} icon={faCalendarDays} tooltipTranslationid="report.period.daily"/>
                            <DiagramButton id="2" buttonCategory="daily" setCategory={setCategory} category={category} label={t('diagrams.period.daily')} icon={faCalendarWeek} tooltipTranslationid="report.period.monthly"/>
                        </ButtonGroup>
                    </Col>
                </Row>
                <MaximizeButton isMaximized={isMaximized} toggleMaximize={toggleMaximize} />
            </CardHeader>
            <CardBody>
                <DiagramBody chartData={formattedChartData} isLoading={isLoading}/>
            </CardBody>
        </Card>
    );
};

export default TokenUsageHistogram;
